import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import { syncGateReason } from "../lib/gate_reason"

// My Vouchers → Set Up / Change Weekly Refill popup (#19616). Presentation only: on
// open and on any choice change the server renders the cost lines (a Turbo
// Stream preview: RefillSetup#charge_now_cents / #first_refill_at). Ready Gate
// (PATTERN-3, INTERACTION-5): Continue stays held, with its reason, until the
// NEWEST preview has arrived with no blocker and its agreement is ticked. A
// change clears the tick, aborts the older preview and takes a new attempt id
// (Stripe's idempotency key). Each preview sends a number the server echoes as
// the summary's data-preview, so the gate never depends on whether the stream
// renders before or after get() resolves. The server re-checks it all
// (RefillSetup#agreed_to?). A Change summary asks no agreement unless the
// amount goes up: with none, it opens on no blocker (RefillChange#blocker).
const PREVIEW_SKIPS = ["agreed", "authenticity_token", "client_attempt_id", "_method"]
const UPDATING = "Updating the cost…"
const FAILED = "The cost could not be updated. Change a choice to try again."

export default class extends Controller {
  static targets = ["form", "attempt", "card", "radio", "summary", "agree", "submit", "reason", "reasonText"]
  static values = { previewUrl: String, delay: { type: Number, default: 250 } }

  connect() {
    this.sync()
  }

  disconnect() {
    clearTimeout(this.timer)
    this.request?.abort()
  }

  // A mode, amount or day change, or show.bs.modal on open: hold
  // Continue now, then one preview once typing settles.
  refresh() {
    this.request?.abort()
    this.previewId = (this.previewId || 0) + 1
    if (this.hasAttemptTarget) this.attemptTarget.value = crypto.randomUUID()
    this.hold(UPDATING)
    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.preview(), this.delayValue)
  }

  async preview() {
    const request = (this.request = new AbortController())
    const query = new FormData(this.formTarget)
    PREVIEW_SKIPS.forEach((name) => query.delete(name))
    query.set("preview", this.previewId)
    try {
      const response = await get(this.previewUrlValue, { query, responseKind: "turbo-stream", signal: request.signal })
      if (request.signal.aborted) return
      if (!response.ok) this.hold(FAILED)
    } catch (error) {
      if (request.signal.aborted) return
      console.error("[voucher-refill-setup] preview failed", error)
      this.hold(FAILED)
    }
  }

  // A new summary brings a fresh, unticked agreement. Only the newest
  // preview's (its data-preview is the latest number sent) reopens the gate to
  // its own blocker: an older one rendered late stays held.
  summaryTargetConnected(summary) {
    if (this.previewId && summary.dataset.preview === String(this.previewId)) this.held = null
    this.sync()
  }

  hold(reason) {
    this.held = reason
    if (this.hasAgreeTarget) this.agreeTarget.checked = false
    this.sync()
  }

  sync() {
    const choice = this.radioTargets.find((radio) => radio.checked)?.value
    this.cardTargets.forEach((card) => card.classList.toggle("selected", card.dataset.choice === choice))
    if (!this.hasSubmitTarget) return

    const blocker = this.hasSummaryTarget ? this.summaryTarget.dataset.blocker : ""
    const ready = !this.held && (this.hasAgreeTarget ? this.agreeTarget.checked : !blocker)
    const reason = ready ? "" : this.held || blocker
    syncGateReason(this.submitTarget, { ready, reason })
    this.reasonTarget.hidden = ready
    this.reasonTextTarget.textContent = reason
  }
}
